import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark';

/**
 * ThemeService - Servicio singleton para gestionar el tema global de la aplicación
 * 
 * Este servicio:
 * - Lee el channel de localStorage para determinar el tema
 * - Si channel === 6, activa el dark mode automáticamente
 * - Aplica/remueve la clase "dark-mode" al elemento <body>
 * - Persiste la preferencia del usuario
 * - Proporciona reactividad mediante BehaviorSubject y Observable
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeSubject = new BehaviorSubject<ThemeMode>(this.getInitialTheme());
  public theme$: Observable<ThemeMode> = this.themeSubject.asObservable();

  constructor() {
    this.initializeTheme();
  }

  /**
   * Determina el tema inicial basado en el channel
   * Si channel === 6, retorna 'dark', de lo contrario 'light'
   */
  private getInitialTheme(): ThemeMode {
    const channel = this.getChannelFromStorage();
    return this.shouldEnableDarkMode(channel) ? 'dark' : 'light';
  }

  /**
   * Obtiene el channel del localStorage
   */
  private getChannelFromStorage(): number | null {
    const channel = localStorage.getItem('channel');
    return channel ? parseInt(channel, 10) : null;
  }

  /**
   * Determina si se debe activar el dark mode basado en el channel
   */
  private shouldEnableDarkMode(channel: number | null): boolean {
    return channel === 6;
  }

  /**
   * Inicializa el tema en el documento
   */
  private initializeTheme(): void {
    const currentTheme = this.themeSubject.value;
    this.applyTheme(currentTheme);
  }

  /**
   * Obtiene el tema actual
   */
  getCurrentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  /**
   * Alterna entre tema claro y oscuro
   */
  toggleTheme(): void {
    const newTheme = this.themeSubject.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Establece un tema específico
   */
  setTheme(theme: ThemeMode): void {
    this.themeSubject.next(theme);
    this.applyTheme(theme);
  }

  /**
   * Aplica el tema al documento HTML y body
   */
  private applyTheme(theme: ThemeMode): void {
    const htmlElement = document.documentElement;
    const body = document.body;
    
    if (theme === 'dark') {
      htmlElement.setAttribute('data-theme', 'dark');
      body.classList.add('dark-mode');
      body.classList.remove('light-mode');
    } else {
      htmlElement.setAttribute('data-theme', 'light');
      body.classList.add('light-mode');
      body.classList.remove('dark-mode');
    }
  }

  /**
   * Verifica si el modo oscuro está activo
   */
  isDarkMode(): boolean {
    return this.themeSubject.value === 'dark';
  }

  /**
   * Reinicializa el tema basado en el channel actual en localStorage
   * Útil cuando el channel cambia después de la inicialización
   */
  public reinitializeTheme(): void {
    const channel = this.getChannelFromStorage();
    const isDarkMode = this.shouldEnableDarkMode(channel);
    const newTheme: ThemeMode = isDarkMode ? 'dark' : 'light';
    
    this.themeSubject.next(newTheme);
    this.applyTheme(newTheme);
  }
}
